/**
 * IngestWizard — the "New integration" / "New version" flow. Steps:
 * kind → (mcp → mcpDiscover → mcpDone) | (source → ingesting → done | duplicate).
 * Verbatim copy, tiers, and progress behavior.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { ArrowRight, Check, ChevronLeft, Close, Cube, Brackets, Plus, Upload, WarningCircle } from '../icons';
import { Button, Select, Spinner } from '../components';
import { useBridge } from '../store/bridgeStore';
import { allInts, nameOf } from '../domain/catalog';
import { versionsFor } from '../domain/versions';
import { deriveName, detectCategory, initialsOf } from '../domain/naming';
import { mcpToolsFor } from '../domain/ingest';
import { TIERS, CATEGORIES, STEP_LABELS, COLORS } from '../data/seeds';

export default function IngestWizard() {
  const { state, actions } = useBridge();
  const ig = state.ingest;
  if (!ig) return null;
  const ints = allInts(state.adapters, state.metaOverrides);
  const catOptions = CATEGORIES.map(c => ({ value: c, label: c }));
  const isMcpFlow = ig.step === 'mcp' || ig.step === 'mcpDiscover' || ig.step === 'mcpDone';
  const title = ig.mode === 'version' ? 'New version of ' + nameOf(ig.targetAdapterId!, ints) : isMcpFlow ? 'Connect MCP server' : 'New integration';
  const subtitle = ig.mode === 'version' ? 'Ingest the updated spec · author a new version mapping' : isMcpFlow ? 'Self-describing · no mapping required' : 'Choose how this tool connects';
  const canGoBack = ig.step === 'mcp' || (ig.step === 'source' && ig.mode !== 'version');

  return (
    <View>
      {React.createElement(
        'div',
        { className: 't-anim-fadein', style: { position: 'fixed', inset: 0, background: colors.overlay, display: 'grid', placeItems: 'center', zIndex: 70, padding: 24 } },
        React.createElement(
          'div',
          { className: 't-anim-slideup', style: { background: colors.card, borderRadius: 12, boxShadow: 'var(--shadow-lg)', width: 540, maxWidth: '100%', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } },
          <View key="h" style={styles.header}>
            {canGoBack && <Pressable onPress={actions.backToKind} style={styles.backBtn}><ChevronLeft size={16} color={colors.ink2} /></Pressable>}
            <View style={styles.headerIcon}><Plus size={16} color={colors.accent} /></View>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.subtitle}>{subtitle}</Text>
            </View>
            <Pressable onPress={actions.closeIngest}><Close size={18} color={colors.ink3} /></Pressable>
          </View>,
          <ScrollView key="b" style={{ maxHeight: '64vh' as any }} contentContainerStyle={{ padding: 22 }}>
            <Steps ig={ig} actions={actions} ints={ints} catOptions={catOptions} state={state} />
          </ScrollView>,
          <View key="f" style={styles.footer}><Footer ig={ig} actions={actions} ints={ints} state={state} /></View>,
        ),
      )}
    </View>
  );
}

function Steps({ ig, actions, ints, catOptions, state }: any) {
  if (ig.step === 'kind') {
    return (
      <>
        <Text style={styles.prompt}>How does this tool connect to SK Bridge?</Text>
        <View style={{ gap: 10 }}>
          <KindCard onPress={() => actions.chooseKind('mcp')} icon={<Cube size={19} color={colors.success} />} iconBg={colors.successSoft} title="MCP server" desc="Self-describing tool schema. Connect, discover tools, and register — no field mapping needed." />
          <KindCard onPress={() => actions.chooseKind('api')} icon={<Brackets size={19} color={colors.info} />} iconBg={colors.infoSoft} title="API integration" desc="Ingest an OpenAPI spec or API docs. We draft a border-translation mapping for you to review." />
        </View>
      </>
    );
  }

  if (ig.step === 'mcp') {
    return (
      <>
        <View style={[styles.infoBox, { borderColor: colors.successLine, backgroundColor: colors.successSoft } as any]}>
          <Text style={[styles.infoText, { color: colors.success } as any]}>MCP servers are self-describing — SK Bridge reads the tool schema directly. No spec ingestion or mapping required.</Text>
        </View>
        <Text style={styles.label}>MCP server URL</Text>
        <TextInput value={ig.mcpUrl} onChangeText={actions.onMcpUrl} placeholder="https://mcp.vendor.com/sse" placeholderTextColor={colors.ink4 as any} style={styles.inputMono} />
        <Text style={[styles.label, { marginTop: 16 }]}>Transport</Text>
        <View style={styles.cdRow}>
          <Pressable onPress={() => actions.setMcpTransport('sse')} style={[styles.cdBtn, { borderColor: ig.mcpTransport === 'sse' ? colors.accent : colors.line } as any]}><Text style={styles.cdText}>SSE</Text></Pressable>
          <Pressable onPress={() => actions.setMcpTransport('stdio')} style={[styles.cdBtn, { borderColor: ig.mcpTransport === 'stdio' ? colors.accent : colors.line } as any]}><Text style={styles.cdText}>streamable-http</Text></Pressable>
        </View>
        <Text style={[styles.label, { marginTop: 16 }]}>Product type</Text>
        <Select value={ig.categoryTouched ? ig.category : detectCategory(ig.mcpUrl || '')} options={catOptions} onChange={actions.onIngestCategory} variant="field" height={38} />
      </>
    );
  }

  if (ig.step === 'mcpDiscover') {
    const name = ig.mcpName || deriveName(ig.mcpUrl || '');
    return (
      <View style={{ alignItems: 'center', paddingVertical: 4 }}>
        <Spinner size={28} />
        <Text style={[styles.title, { marginTop: 12 }]}>{'Connecting to ' + name + '…'}</Text>
        <Text className="t-mono" style={styles.discSub}>handshake · listing tools</Text>
      </View>
    );
  }

  if (ig.step === 'mcpDone') {
    const name = ig.mcpName || deriveName(ig.mcpUrl || '');
    const initials = initialsOf(name) || 'MC';
    const color = COLORS[state.adapters.length % COLORS.length];
    const cat = ig.categoryTouched ? ig.category : detectCategory(ig.mcpUrl || '');
    const tools = mcpToolsFor(cat || 'CRM', false);
    return (
      <>
        <View style={styles.doneHead}>
          <View style={[styles.doneAvatar, { backgroundColor: color } as any]}><Text style={styles.doneAvatarText}>{initials}</Text></View>
          <View style={{ flex: 1 }}>
            <Text style={styles.doneName}>{name}</Text>
            <Text className="t-mono" style={styles.doneSub}>MCP adapter · self-describing</Text>
          </View>
          <View style={styles.connected}><Check size={11} color={colors.success} /><Text style={styles.connectedText}>Connected</Text></View>
        </View>
        <Text style={styles.sectionLabel}>{'Discovered tools · ' + tools.length + ' tools'}</Text>
        <View style={{ gap: 6, marginBottom: 16 }}>
          {tools.map((t, i) => (
            <View key={i} style={styles.tool}><View style={styles.toolDot} /><Text className="t-mono" style={[styles.toolName, { flex: 1 }]}>{t.name}</Text><Text style={styles.toolKind}>{t.kind}</Text></View>
          ))}
        </View>
        <View style={styles.infoBox}><Text style={styles.infoText}>Registers directly as a connector — MCP tools are invoked natively, so there's no border-translation mapping to review.</Text></View>
      </>
    );
  }

  if (ig.step === 'source') {
    const T = TIERS[ig.sourceType as 'openapi' | 'prose'];
    return (
      <>
        {ig.mode === 'version' && (
          <View style={styles.versionBox}>
            <Text style={styles.label}>New vendor version</Text>
            <View style={styles.versionRow}>
              <Text className="t-mono" style={styles.catalogX}>{'v1 ×'}</Text>
              <TextInput value={ig.versionVendor} onChangeText={actions.onVersionVendor} placeholder="e.g. 2.8" placeholderTextColor={colors.ink4 as any} style={[styles.inputMono, { flex: 1, height: 36 }]} />
            </View>
            <Text style={styles.versionHint}>The current live version keeps serving until this new mapping is approved.</Text>
          </View>
        )}
        <Text style={styles.label}>{ig.mode === 'version' ? "Point SK Bridge at the new version's interface" : 'What kind of API source do you have?'}</Text>
        <View style={styles.sourceCards}>
          <SourceCard active={ig.sourceType === 'openapi'} onPress={() => actions.setIngestSource('openapi')} tone="info" title="API spec" badge="Structured · high confidence" desc="OpenAPI / Swagger JSON or YAML." />
          <SourceCard active={ig.sourceType === 'prose'} onPress={() => actions.setIngestSource('prose')} tone="warn" title="API docs" badge="Unstructured · review needed" desc="Human-readable API page." />
        </View>
        <Text style={[styles.label, { marginTop: 2 }]}>{T.urlLabel}</Text>
        <TextInput value={ig.url} onChangeText={actions.onIngestUrl} placeholder={T.ph} placeholderTextColor={colors.ink4 as any} style={styles.inputMono} />
        {ig.sourceType === 'openapi' && (
          <Pressable onPress={actions.fakeUpload} style={styles.upload}>
            <Upload size={20} color={colors.ink3} />
            <Text style={styles.uploadText}>or upload <Text className="t-mono" style={{ color: colors.ink2 } as any}>openapi.json / .yaml</Text></Text>
          </Pressable>
        )}
        {ig.mode !== 'version' && (
          <View style={{ marginTop: 16 }}>
            <Text style={styles.label}>Product type</Text>
            <Select value={ig.categoryTouched ? ig.category : detectCategory(ig.url)} options={CATEGORIES.map(c => ({ value: c, label: c }))} onChange={actions.onIngestCategory} variant="field" height={38} />
            <Text style={styles.versionHint}>Groups this connector in the directory. Auto-detected from the spec — change if needed.</Text>
          </View>
        )}
        <View style={[styles.tierBox, { borderColor: `var(--${T.tone}-line)`, backgroundColor: `var(--${T.tone}-soft)` } as any]}>
          <View style={[styles.tierDot, { backgroundColor: `var(--${T.tone})` } as any]} />
          <View style={{ flex: 1 }}>
            <Text style={[styles.tierLabel, { color: `var(--${T.tone})` } as any]}>{T.label}</Text>
            <Text style={styles.tierNote}>{T.note}</Text>
          </View>
        </View>
      </>
    );
  }

  if (ig.step === 'duplicate') {
    const dupVs = ig.dupAdapterId ? versionsFor(ig.dupAdapterId, state.versions, state.adapters) : [];
    const dupExists = ig.dupAdapterId ? versionsFor(ig.dupAdapterId, state.versions, state.adapters).some((v: any) => v.vendor === (ig.versionVendor || '').trim()) : false;
    return (
      <>
        <View style={[styles.infoBox, { borderColor: colors.warnLine, backgroundColor: colors.warnSoft, flexDirection: 'row', gap: 10 } as any]}>
          <WarningCircle size={16} color={colors.warn} />
          <View style={{ flex: 1 }}>
            <Text style={[styles.tierLabel, { color: colors.warn } as any]}>{ig.detectedName + ' is already an integration'}</Text>
            <Text style={styles.tierNote}>SK Bridge keeps one adapter per vendor. If this spec is a newer API version, add it as a new version below.</Text>
          </View>
        </View>
        <Text style={styles.sectionLabel}>Existing versions</Text>
        <View style={[styles.chips, { marginBottom: 18 }]}>
          {dupVs.map((v: any, i: number) => {
            const tone = v.status === 'published' ? 'success' : v.status === 'draft' ? 'warn' : 'neutral';
            return <View key={i} style={[styles.verChip, { borderColor: `var(--${tone}-line)`, backgroundColor: `var(--${tone}-soft)` } as any]}><Text className="t-mono" style={[styles.verChipText, { color: `var(--${tone})` } as any]}>{'v1 × ' + v.vendor + ' · ' + (v.status === 'published' ? 'live' : v.status)}</Text></View>;
          })}
        </View>
        <Text style={styles.label}>Add as a new vendor version</Text>
        <View style={styles.versionRow}>
          <Text className="t-mono" style={styles.catalogX}>{'v1 ×'}</Text>
          <TextInput value={ig.versionVendor} onChangeText={actions.onVersionVendor} placeholder="e.g. 2.8" placeholderTextColor={colors.ink4 as any} style={[styles.inputMono, { flex: 1 }]} />
        </View>
        {dupExists && (
          <View style={styles.dupErr}><WarningCircle size={14} color={colors.danger} /><Text style={styles.dupErrText}>That version already exists — this would be a duplicate.</Text></View>
        )}
      </>
    );
  }

  if (ig.step === 'ingesting') {
    const T = TIERS[ig.sourceType as 'openapi' | 'prose'];
    return (
      <>
        <View style={{ alignItems: 'center', marginBottom: 18 }}>
          <Spinner size={28} />
          <Text style={[styles.title, { marginTop: 12 }]}>{'Ingesting ' + ig.detectedName + '…'}</Text>
          <Text className="t-mono" style={styles.discSub}>{T.label}</Text>
        </View>
        <View style={{ gap: 4 }}>
          {STEP_LABELS.map((label, i) => {
            const done = i < ig.progress;
            const active = i === ig.progress;
            return (
              <View key={i} style={[styles.stepRow, { backgroundColor: active ? colors.accentSoft : 'transparent' } as any]}>
                <View style={[styles.stepDot, { borderColor: done ? colors.success : active ? colors.accent : colors.lineStrong, backgroundColor: done ? colors.success : 'transparent' } as any]}>
                  {done && <Check size={11} color="#fff" />}
                </View>
                <Text style={[styles.stepLabel, { color: done ? colors.ink2 : active ? colors.ink : colors.ink4, fontWeight: active ? fontWeight.semibold : fontWeight.regular } as any]}>{label}</Text>
              </View>
            );
          })}
        </View>
      </>
    );
  }

  if (ig.step === 'done') {
    const r = ig.result;
    return (
      <>
        <View style={styles.doneHead}>
          <View style={[styles.doneAvatar, { backgroundColor: r.adapter.color } as any]}><Text style={styles.doneAvatarText}>{r.adapter.initials}</Text></View>
          <View style={{ flex: 1 }}>
            <Text style={styles.doneName}>{ig.detectedName}</Text>
            <Text className="t-mono" style={styles.doneSub}>{r.classLabel + ' adapter · auto-drafted'}</Text>
          </View>
          <View style={styles.connected}><Check size={11} color={colors.success} /><Text style={styles.connectedText}>Drafted</Text></View>
        </View>
        <View style={styles.resStats}>
          <ResStat big={r.conf} label="mean confidence" color={colors.accent} />
          <ResStat big={r.edges} label="edges auto-mapped" color={colors.ink} />
          <ResStat big={String(r.needs)} label="need review" color={colors.warn} warn />
        </View>
        <View style={styles.resMeta}>
          <View style={styles.resMetaBox}><Text style={styles.resMetaKey}>Auth flow detected</Text><Text className="t-mono" style={styles.resMetaVal}>{r.auth}</Text></View>
          <View style={styles.resMetaBox}><Text style={styles.resMetaKey}>Change delivery</Text><Text className="t-mono" style={styles.resMetaVal}>{r.change}</Text></View>
        </View>
        <View style={[styles.infoBox, { marginTop: 14 }]}><Text style={styles.infoText}>Draft only. Nothing is live until you resolve the flagged fields and approve — the mapping commits as a deterministic <Text className="t-mono">graph_spec</Text>.</Text></View>
      </>
    );
  }

  return null;
}

function Footer({ ig, actions, ints, state }: any) {
  if (ig.step === 'kind') return <Button label="Cancel" variant="ghost" onPress={actions.closeIngest} />;
  if (ig.step === 'mcp') {
    const ok = !!(ig.mcpUrl || '').trim();
    return (
      <>
        <Button label="Back" variant="ghost" onPress={actions.backToKind} />
        <Button label="Connect & discover" onPress={actions.connectMcp} disabledLook={!ok} />
      </>
    );
  }
  if (ig.step === 'mcpDiscover') return <Button label="Connecting…" variant="secondary" />;
  if (ig.step === 'mcpDone') return <Button label="Add to directory" onPress={actions.addMcp} />;
  if (ig.step === 'source') {
    const ok = !!ig.url.trim();
    return (
      <>
        <Button label={ig.mode === 'version' ? 'Cancel' : 'Back'} variant="ghost" onPress={() => (ig.mode === 'version' ? actions.closeIngest() : actions.backToKind())} />
        <Button label="Begin ingestion" onPress={actions.beginIngest} disabledLook={!ok} />
      </>
    );
  }
  if (ig.step === 'ingesting') return <Button label="Working…" variant="secondary" />;
  if (ig.step === 'duplicate') {
    return (
      <>
        <Button label="Open existing" variant="ghost" onPress={actions.openDupExisting} />
        <Button label="Add as new version" onPress={actions.addVersionFromDuplicate} />
      </>
    );
  }
  if (ig.step === 'done') return <Button label="Open in editor" onPress={actions.openMapping} right={<ArrowRight size={14} color="#fff" />} />;
  return null;
}

function KindCard({ onPress, icon, iconBg, title, desc }: any) {
  return (
    <Pressable onPress={onPress} style={styles.kindCard}>
      <View style={[styles.kindIcon, { backgroundColor: iconBg } as any]}>{icon}</View>
      <View style={{ flex: 1 }}>
        <Text style={styles.kindTitle}>{title}</Text>
        <Text style={styles.kindDesc}>{desc}</Text>
      </View>
      <View style={{ marginTop: 10 }}><ArrowRight size={16} color={colors.ink4} /></View>
    </Pressable>
  );
}
function SourceCard({ active, onPress, tone, title, badge, desc }: any) {
  return (
    <Pressable onPress={onPress} style={[styles.sourceCard, { borderColor: active ? `var(--${tone})` : colors.line, backgroundColor: active ? `var(--${tone}-soft)` : colors.card } as any]}>
      <Text style={styles.sourceTitle}>{title}</Text>
      <Text style={[styles.sourceBadge, { color: `var(--${tone})` } as any]}>{badge}</Text>
      <Text style={styles.sourceDesc}>{desc}</Text>
    </Pressable>
  );
}
function ResStat({ big, label, color, warn }: any) {
  return (
    <View style={[styles.resStat, warn ? { borderColor: colors.warnLine, backgroundColor: colors.warnSoft } : null]}>
      <Text style={[styles.resStatBig, { color } as any]}>{big}</Text>
      <Text style={styles.resStatLabel}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: { padding: 18, paddingHorizontal: 22, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 10 },
  backBtn: { width: 30, height: 30, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' } as any,
  headerIcon: { width: 30, height: 30, borderRadius: radii.r3, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center' },
  title: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  subtitle: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  prompt: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 12 },
  kindCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 16, borderRadius: radii.r3, borderWidth: 1.5, borderColor: colors.lineStrong, backgroundColor: colors.paper2, cursor: 'pointer' } as any,
  kindIcon: { width: 38, height: 38, borderRadius: radii.r3, alignItems: 'center', justifyContent: 'center' },
  kindTitle: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.ink },
  kindDesc: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3, marginTop: 3, lineHeight: 18 },
  infoBox: { padding: 11, paddingHorizontal: 13, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r3, marginBottom: 18 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
  label: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 6 },
  inputMono: { width: '100%', height: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: radii.r2, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink as any, outlineStyle: 'none' } as any,
  cdRow: { flexDirection: 'row', gap: 8 },
  cdBtn: { flex: 1, paddingVertical: 10, borderRadius: radii.r2, borderWidth: 1.5, backgroundColor: colors.paper2, alignItems: 'center', cursor: 'pointer' } as any,
  cdText: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink },
  discSub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 },
  doneHead: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 18 },
  doneAvatar: { width: 42, height: 42, borderRadius: radii.r3, alignItems: 'center', justifyContent: 'center' },
  doneAvatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, color: '#fff' },
  doneName: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  doneSub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  connected: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 24, paddingHorizontal: 10, borderRadius: radii.full, borderWidth: 1, borderColor: colors.successLine, backgroundColor: colors.successSoft },
  connectedText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: colors.success },
  sectionLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.ink3, fontWeight: fontWeight.semibold, marginBottom: 8 },
  tool: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.paper2 },
  toolDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.success },
  toolName: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink },
  toolKind: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  versionBox: { marginBottom: 18, padding: 12, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2 },
  versionRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  catalogX: { fontFamily: fontFamily.mono, fontSize: fontSize.md, color: colors.ink3 },
  versionHint: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 8, lineHeight: 18 },
  sourceCards: { flexDirection: 'row', gap: 8, marginBottom: 18 },
  sourceCard: { flex: 1, padding: 12, borderRadius: radii.r3, borderWidth: 1.5, cursor: 'pointer' } as any,
  sourceTitle: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.ink },
  sourceBadge: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium, marginTop: 3 },
  sourceDesc: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 4, lineHeight: 16 },
  upload: { marginTop: 10, padding: 16, borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.lineStrong, borderRadius: radii.r3, backgroundColor: colors.paper2, alignItems: 'center', gap: 6, cursor: 'pointer' } as any,
  uploadText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3 },
  tierBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 9, marginTop: 16, padding: 12, paddingHorizontal: 14, borderWidth: 1, borderRadius: radii.r3 },
  tierDot: { width: 8, height: 8, borderRadius: 4, marginTop: 4 },
  tierLabel: { fontFamily: fontFamily.sans, fontWeight: fontWeight.medium, fontSize: fontSize.sm },
  tierNote: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink2, marginTop: 2, lineHeight: 18 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  verChip: { height: 26, paddingHorizontal: 10, borderRadius: radii.r2, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  verChipText: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, fontWeight: fontWeight.medium },
  dupErr: { flexDirection: 'row', alignItems: 'center', gap: 7, marginTop: 10 },
  dupErrText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.danger },
  stepRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 12, borderRadius: radii.r2 },
  stepDot: { width: 18, height: 18, borderRadius: 9, borderWidth: 1.5, alignItems: 'center', justifyContent: 'center' },
  stepLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.sm },
  resStats: { flexDirection: 'row', gap: 10, marginBottom: 16 },
  resStat: { flex: 1, alignItems: 'center', padding: 13, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2 },
  resStatBig: { fontFamily: fontFamily.sans, fontSize: fontSize.xxl, fontWeight: fontWeight.semibold },
  resStatLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 },
  resMeta: { flexDirection: 'row', gap: 10 },
  resMetaBox: { flex: 1, padding: 11, paddingHorizontal: 13, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2 },
  resMetaKey: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4 },
  resMetaVal: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink, marginTop: 3 },
  footer: { padding: 14, paddingHorizontal: 22, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexDirection: 'row', justifyContent: 'flex-end', gap: 10 },
});
